import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { FaChevronDown, FaChevronRight, FaExternalLinkAlt } from 'react-icons/fa';
import './Entrepreneurship.css';

const Entrepreneurship = () => {
  const [ref] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const [expandedSections, setExpandedSections] = useState({
    ventures: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const allProjects = [
    {
      title: "DataNXT",
      role: "Co-Founder & Academic Advisor",
      description: "A cutting-edge platform focused on data-driven reporting and financial analysis.",
      website: "https://datanxt.de/",
      status: "Active",
      highlights: [
        "Automated data extraction from financial documents",
        "Intelligent structuring and digesting of financial information",
        "Customized automated reporting workflows",
        "Advanced assessment and content analysis capabilities",
      ]
    },
    {
      title: "Essence Scholar",
      role: "Co-Founder & Academic Advisor",
      description: "An AI-powered academic research platform that transforms how researchers interact with academic literature. The platform provides personalized insights, author profiles, and interactive chat capabilities for every research paper, helping scholars see papers through their academic lens.",
      website: "https://www.essencescholar.com",
      status: "Active",
      highlights: [
        "Smart PDF reading for academic papers",
        "Interactive chat using RAG for enhanced research understanding",
        "Personalized insights matching research interests and academic focus",
      ],
      // metrics: {
      //   papersAnalyzed: "10,000+",
      //   activeResearchers: "500+",
      //   analysisTime: "30-120s",
      //   accuracyRate: "95%"
      // }
    },
    {
      title: "Dealcode AI",
      role: "AI Advisor",
      description: "Europe's leading Sales AI platform that automates lead generation, lead qualification, and personalized outreach with AI agents. Led the machine learning and artificial intelligence development team.",
      website: "https://www.dealcode.ai",
      status: "Past Role",
      period: "Previous Position (2021-2022)",
      highlights: [
        "Led machine learning and AI development team",
        "Enhanced sales efficiency through AI automation"
      ],
      // note: "This was a previous role and I am no longer affiliated with the company."
    }
  ];

  return (
    <section id="entrepreneurship" className="entrepreneurship section" ref={ref}>
      <div className="container">
        <div className="section-title">
          <h2>Entrepreneurship</h2>
          <p>My startup experiences and entrepreneurial ventures</p>
        </div>

        <div className="entrepreneurship-intro">
          <p>
            Beyond academia, I have been actively involved in the startup ecosystem, 
            contributing to innovative ventures that bridge the gap between research 
            and practical applications. My entrepreneurial journey spans from leading 
            AI development teams to founding academic-focused platforms.
          </p>
        </div>

        {/* All Projects */}
        <div className="entrepreneurship-section">
          <button 
            className="section-header"
            onClick={() => toggleSection('ventures')}
          >
            {expandedSections.ventures ? <FaChevronDown /> : <FaChevronRight />}
            <h3>Entrepreneurial Ventures</h3>
          </button>

          {expandedSections.ventures && (
            <div className="section-content">
              <div className="projects-grid">
                {allProjects.map((project, index) => (
                  <div key={index} className={`project-card ${project.status === 'Active' ? 'current' : 'past'}`}>
                    <div className="project-header">
                      <h4>{project.title}</h4>
                      <span className={`status-badge ${project.status === 'Active' ? 'active' : 'past'}`}>
                        {project.status}
                      </span>
                    </div>
                    <p className="project-role">{project.role}</p>
                    {project.period && (
                      <p className="project-period">{project.period}</p>
                    )}
                    <p className="project-description">{project.description}</p>
                    
                    <div className="project-highlights">
                      <h5>{project.status === 'Active' ? 'Highlights:' : 'Key Achievements:'}</h5>
                      <ul>
                        {project.highlights.map((highlight, idx) => (
                          <li key={idx}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    {project.metrics && (
                      <div className="project-metrics">
                        <h5>Platform Metrics:</h5>
                        <div className="metrics-grid">
                          <div className="metric-item">
                            <span className="metric-value">{project.metrics.papersAnalyzed}</span>
                            <span className="metric-label">Papers Analyzed</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-value">{project.metrics.activeResearchers}</span>
                            <span className="metric-label">Active Researchers</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-value">{project.metrics.analysisTime}</span>
                            <span className="metric-label">Analysis Time</span>
                          </div>
                          <div className="metric-item">
                            <span className="metric-value">{project.metrics.accuracyRate}</span>
                            <span className="metric-label">Accuracy Rate</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {project.note && (
                      <div className="project-note">
                        <p><strong>Note:</strong> {project.note}</p>
                      </div>
                    )}

                    <div className="project-actions">
                      <a 
                        href={project.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`btn ${project.status === 'Active' ? 'btn-secondary' : 'btn-outline'}`}
                      >
                        {project.status === 'Active' ? 'Visit Website' : 'View Company'} <FaExternalLinkAlt />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Entrepreneurship; 